import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Autocomplete, CircularProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ButtonComponent from "components/common/ButtonComponent";
import { addGlobalAlertState } from "store/global-alert/GlobalAlertActions";
import DropdownMenu from "components/common/DropdownMenu";
import AppState from "store/AppState";
import {
  selectCurrentStoreId,
  selectStoreInfoById,
} from "store/store-list/StoreListSelectors";
import {
  clearLinkedCategorySearch,
  fetchAllLinkedCategories,
  addLinkedCategorySetsRequest,
  fetchLinkedCategoryCodes,
  resetUploadSuccess,
  searchLinkedCategories,
} from "store/linked-categories/LinkedCategoriesActions";
import {
  selectCategorySearchLoading,
  selectCategorySearchResults,
  selectLinkedCategories,
  selectLinkedCategoriesLoading,
  selectLinkedCategoryLabels,
  selectLinkedCategoriesUploadProgress,
  selectLinkedCategoriesUploadSuccess,
} from "store/linked-categories/LinkedCategoriesSelectors";
import {
  CategoryLabel,
  CategorySearchResult,
  LinkedCategoryEntry,
  LinkedCategoryPane,
} from "store/linked-categories/LinkedCategoriesTypes";
import {
  ActionButtonBox,
  ActionColumn,
  Container,
  PaneWrapper,
  CategorySetBox,
  CategorySetRow,
  LinkingOverlay,
  StatusMessage,
  SearchField,
} from "./LinkedCategoriesSearchStyles";

const DEBOUNCE_DELAY = 500;
const MAX_OPTIONS = 5;
const DELIMITER = " : ";

export const categoryKey = (
  category: CategorySearchResult | LinkedCategoryEntry,
): string => `${category.storeId}:${category.catalogId}:${category.categoryId}`;

// Legacy labels a category "identifier : owning catalog : owning store", and
// takes all three from the server rather than resolving any of them here.
// A link whose category no longer exists has no label, so it shows the ids
// that were stored for it, which is all legacy ever displayed.
export const formatCategory = (
  category: CategorySearchResult | LinkedCategoryEntry,
  labels: Record<string, CategoryLabel>,
): string => {
  const label = labels[categoryKey(category)];
  return label?.code
    ? [label.code, label.catalogCode, label.storeCode].join(DELIMITER)
    : [category.categoryId, category.catalogId, category.storeId].join(
        DELIMITER,
      );
};

const isSameCategory = (
  a: LinkedCategoryEntry,
  b: CategorySearchResult | null,
): boolean =>
  !!b &&
  a.categoryId === b.categoryId &&
  a.catalogId === b.catalogId &&
  a.storeId === b.storeId;

interface SearchPaneProps {
  pane: LinkedCategoryPane;
  selected: CategorySearchResult | null;
  onSelect: (category: CategorySearchResult | null) => void;
  categorySet: LinkedCategoryEntry[];
  checked: string[];
  onRowClick: (key: string, event: React.MouseEvent) => void;
  statusOverride?: string;
  onUnlink?: () => void;
  unlinkDisabled?: boolean;
}

const SearchPane = ({
  pane,
  selected,
  onSelect,
  categorySet,
  checked,
  onRowClick,
  statusOverride,
  onUnlink,
  unlinkDisabled,
}: SearchPaneProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentStoreId = useSelector(selectCurrentStoreId);
  const [storeId, setStoreId] = useState<string>(currentStoreId ?? "");
  const storeInfo = useSelector((state: AppState) =>
    selectStoreInfoById(state, storeId),
  );

  const catalogId = storeInfo?.storeDefaults?.catalogId ?? "";
  const results = useSelector((state: AppState) =>
    selectCategorySearchResults(state, pane),
  );
  const loading = useSelector((state: AppState) =>
    selectCategorySearchLoading(state, pane),
  );
  const setsLoading = useSelector(selectLinkedCategoriesLoading);
  const resolvedLabels = useSelector(selectLinkedCategoryLabels);

  // Search results are already labelled; stored set members are resolved.
  const labels = useMemo(
    () => ({
      ...resolvedLabels,
      ...Object.fromEntries(
        results.map((r) => [
          categoryKey(r),
          { code: r.code, catalogCode: r.catalogCode, storeCode: r.storeCode },
        ]),
      ),
    }),
    [resolvedLabels, results],
  );
  const label = useCallback(
    (category: CategorySearchResult | LinkedCategoryEntry) =>
      formatCategory(category, labels),
    [labels],
  );
  const [inputValue, setInputValue] = useState("");
  const previousSelected = useRef(selected);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Clearing the selection from outside the pane empties its search box too.
  useEffect(() => {
    if (previousSelected.current && !selected) setInputValue("");
    previousSelected.current = selected;
  }, [selected]);

  const runSearch = useCallback(
    (startsWith: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const trimmed = startsWith.trim();
      if (!trimmed || !storeId || !catalogId) {
        dispatch(clearLinkedCategorySearch(pane));
        return;
      }
      debounceRef.current = setTimeout(() => {
        dispatch(
          searchLinkedCategories({
            pane,
            storeId,
            catalogId,
            startsWith: trimmed,
            limit: MAX_OPTIONS,
          }),
        );
      }, DEBOUNCE_DELAY);
    },
    [catalogId, dispatch, pane, storeId],
  );

  // Resolve identifiers for set members the search has not already labelled.
  useEffect(() => {
    const unresolved = categorySet.filter(
      (category) => !labels[categoryKey(category)],
    );
    if (unresolved.length) dispatch(fetchLinkedCategoryCodes(unresolved));
  }, [categorySet, labels, dispatch]);

  const statusMessage = statusOverride
    ? statusOverride
    : !selected
      ? ""
      : setsLoading
        ? intl.formatMessage({
            id: "linkedCategoriesSearch.loadingSet",
            defaultMessage: "Loading linked categories...",
          })
        : categorySet.length <= 1
          ? intl.formatMessage({
              id: "linkedCategoriesSearch.noLinkedCategories",
              defaultMessage: "No linked categories found.",
            })
          : intl.formatMessage(
              {
                id: "linkedCategoriesSearch.linkedCategoryCount",
                defaultMessage:
                  "{count, plural, one {# category} other {# categories}} linked to the category selected.",
              },
              { count: categorySet.length - 1 },
            );

  const handleStoreChange = (nextStoreId: string) => {
    setStoreId(nextStoreId);
    setInputValue("");
    onSelect(null);
    dispatch(clearLinkedCategorySearch(pane));
  };

  return (
    <PaneWrapper>
      <Typography variant="title">
        {intl.formatMessage({
          id: "linkedCategoriesSearch.selectStore",
          defaultMessage: "Select a store:",
        })}
      </Typography>
      <DropdownMenu
        menuId="LinkedCategoriesStore"
        name={pane}
        value={storeId}
        changeHandler={(event) => handleStoreChange(event.target.value)}
      />

      <Typography variant="title">
        {intl.formatMessage({
          id: "linkedCategoriesSearch.searchLabel",
          defaultMessage:
            "Type in Category Identifier to search (case sensitive):",
        })}
      </Typography>
      <Autocomplete
        id={`lcs-search-${pane}`}
        options={results}
        filterOptions={(options) => options}
        getOptionLabel={(option) => label(option)}
        isOptionEqualToValue={(option, value) =>
          option.categoryId === value.categoryId &&
          option.catalogId === value.catalogId &&
          option.storeId === value.storeId
        }
        value={selected}
        // Autocomplete drops a selection identical by reference to the value it
        // holds, so a copy is kept to let the same category be picked again.
        onChange={(event, value) => onSelect(value ? { ...value } : null)}
        inputValue={inputValue}
        onInputChange={(event, value, reason) => {
          setInputValue(value);
          if (reason === "input") runSearch(value);
        }}
        loading={loading}
        noOptionsText={intl.formatMessage({
          id: "linkedCategoriesSearch.typeToSearch",
          defaultMessage: "Type to search",
        })}
        size="small"
        renderInput={(params) => (
          <SearchField
            {...params}
            placeholder={intl.formatMessage({
              id: "linkedCategoriesSearch.placeholder",
              defaultMessage:
                "Category Identifier : Owning Catalog Identifier : Owning Store Identifier",
            })}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <StatusMessage variant="text">{statusMessage}</StatusMessage>
      <CategorySetBox data-testid={`lcs-category-set-${pane}`} tabIndex={0}>
        {categorySet.map((category) => (
          <CategorySetRow
            variant="linkedCategoriesText"
            key={categoryKey(category)}
            title={label(category)}
            anchor={isSameCategory(category, selected)}
            selected={checked.includes(categoryKey(category))}
            onClick={(event) => onRowClick(categoryKey(category), event)}
          >
            {label(category)}
          </CategorySetRow>
        ))}
      </CategorySetBox>
      {onUnlink && (
        <ActionButtonBox>
          <ButtonComponent
            color="primary"
            variant="contained"
            fullWidth
            justifyContent="center"
            onClick={onUnlink}
            disabled={unlinkDisabled}
          >
            {intl.formatMessage({
              id: "linkedCategoriesSearch.unlink",
              defaultMessage: "Unlink",
            })}
          </ButtonComponent>
        </ActionButtonBox>
      )}
    </PaneWrapper>
  );
};

const emptyByPane = <T,>(value: T): Record<LinkedCategoryPane, T> => ({
  target: value,
  source: value,
});

export const LinkedCategoriesSearch = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const allLinkedSets = useSelector(selectLinkedCategories);
  const linking = useSelector(selectLinkedCategoriesUploadProgress);
  const linked = useSelector(selectLinkedCategoriesUploadSuccess);
  const [selected, setSelected] = useState<
    Record<LinkedCategoryPane, CategorySearchResult | null>
  >(emptyByPane(null));
  const [checked, setChecked] = useState<Record<LinkedCategoryPane, string[]>>(
    () => emptyByPane<string[]>([]),
  );
  const anchors = useRef<Record<LinkedCategoryPane, string | null>>(
    emptyByPane<string | null>(null),
  );

  useEffect(() => {
    dispatch(fetchAllLinkedCategories());
    // Any success left over from the file upload tool is not ours to report.
    dispatch(resetUploadSuccess());
  }, [dispatch]);

  const setOf = useCallback(
    (category: CategorySearchResult | null) =>
      category
        ? allLinkedSets.find((set) =>
            set.categories.some((member) => isSameCategory(member, category)),
          )
        : undefined,
    [allLinkedSets],
  );

  // Legacy lists the whole group with the selected category included, and
  // falls back to the selected category alone when it belongs to no group.
  // The searched category leads, since the stored order is not meaningful.
  const categorySetOf = useCallback(
    (category: CategorySearchResult | null): LinkedCategoryEntry[] => {
      if (!category) return [];
      const group = setOf(category);
      if (!group || group.categories.length === 0) return [category];
      return [
        ...group.categories.filter((member) =>
          isSameCategory(member, category),
        ),
        ...group.categories.filter(
          (member) => !isSameCategory(member, category),
        ),
      ];
    },
    [setOf],
  );

  // Unlinking the searched category leaves it in no set, so deriving the pane
  // from it would show it again on its own. Once a set has been edited the pane
  // follows that set until a new category is searched.
  const [editedSetId, setEditedSetId] = useState<string | null>(null);
  const targetSet = useMemo(() => {
    if (!editedSetId) return categorySetOf(selected.target);
    const edited = allLinkedSets.find(
      (set) => set.linkedCategorySetId === editedSetId,
    );
    return edited ? edited.categories : [];
  }, [allLinkedSets, categorySetOf, editedSetId, selected.target]);
  const targetSetId =
    editedSetId ?? setOf(selected.target)?.linkedCategorySetId;
  const sourceSet = useMemo(
    () => categorySetOf(selected.source),
    [categorySetOf, selected.source],
  );

  // Categories left behind by a partial link stay in the source pane. The pane
  // is only cleared when the searched category itself was linked, since it then
  // belongs to the target set and both panes would show the same list.
  const clearSourceOnLink = useRef(true);
  const [removed, setRemoved] = useState<number | null>(null);

  useEffect(() => {
    if (!linked) return;
    setChecked(emptyByPane<string[]>([]));
    if (!clearSourceOnLink.current) return;
    setSelected((current) => ({ ...current, source: null }));
    dispatch(clearLinkedCategorySearch("source"));
  }, [linked, dispatch]);

  const handleSelect = useCallback(
    (pane: LinkedCategoryPane, category: CategorySearchResult | null) => {
      setSelected((current) => ({ ...current, [pane]: category }));
      setChecked((current) => ({ ...current, [pane]: [] }));
      anchors.current[pane] = null;
      setRemoved(null);
      if (pane === "target") setEditedSetId(null);
      dispatch(resetUploadSuccess());
    },
    [dispatch],
  );

  // A plain click replaces the selection, Cmd/Ctrl toggles one row, and
  // Shift takes the range from the last row clicked.
  const handleRowClick = useCallback(
    (pane: LinkedCategoryPane, key: string, event: React.MouseEvent) => {
      const keys = (pane === "target" ? targetSet : sourceSet).map(categoryKey);
      setChecked((current) => {
        const paneChecked = current[pane];
        if (event.shiftKey) {
          const anchor = anchors.current[pane];
          const to = keys.indexOf(key);
          const anchored = anchor ? keys.indexOf(anchor) : -1;
          const from = anchored === -1 ? to : anchored;
          const [start, end] = from > to ? [to, from] : [from, to];
          return { ...current, [pane]: keys.slice(start, end + 1) };
        }
        anchors.current[pane] = key;
        if (event.metaKey || event.ctrlKey) {
          return {
            ...current,
            [pane]: paneChecked.includes(key)
              ? paneChecked.filter((entry) => entry !== key)
              : [...paneChecked, key],
          };
        }
        return { ...current, [pane]: [key] };
      });
    },
    [sourceSet, targetSet],
  );

  // Legacy only offers the link once both panes hold a category set.
  const linkDisabled = sourceSet.length === 0 || targetSet.length === 0;

  const handleLink = () => {
    // Legacy refuses to merge a group, or a category, with itself.
    const targetGroupId = setOf(selected.target)?.linkedCategorySetId;
    const sourceGroupId = setOf(selected.source)?.linkedCategorySetId;
    const sameGroup =
      targetGroupId && sourceGroupId
        ? targetGroupId === sourceGroupId
        : !targetGroupId &&
          !sourceGroupId &&
          !!selected.target &&
          isSameCategory(selected.target, selected.source);
    if (sameGroup) {
      dispatch(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "linkedCategories.identicalGroups",
                defaultMessage:
                  "Cannot merge identical linked category groups. Please make sure the selected two groups are different.",
              },
              severity: "error",
              variant: "standard",
            },
          ],
        }),
      );
      return;
    }

    // Nothing selected links the whole source set, as the ticket specifies.
    const picked = checked.source.length
      ? sourceSet.filter((category) =>
          checked.source.includes(categoryKey(category)),
        )
      : sourceSet;
    clearSourceOnLink.current = picked.some((category) =>
      isSameCategory(category, selected.source),
    );
    // A category already in the target group is a duplicate, not a merge.
    const duplicate = picked.find((category) =>
      targetSet.some((member) => categoryKey(member) === categoryKey(category)),
    );
    if (duplicate) {
      dispatch(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "linkedCategories.duplicateCategory",
                defaultMessage:
                  "Duplicate categoryId/catalogId/storeId combination not allowed within the same linked category set. Found duplicate: {categoryId}/{catalogId}/{storeId}",
              },
              variables: {
                categoryId: duplicate.categoryId,
                catalogId: duplicate.catalogId,
                storeId: duplicate.storeId,
              },
              severity: "error",
              variant: "standard",
            },
          ],
        }),
      );
      return;
    }
    const merged = [...targetSet, ...picked];

    // A target that belongs to no set yet is sent empty, as the tool does.
    const linkedCategorySetId = targetSetId ?? "";
    dispatch(
      addLinkedCategorySetsRequest({
        isLinkRequest: true,
        linkedCategorySets: [
          {
            linkedCategorySetId,
            categories: merged.map((category) => ({
              catalogId: category.catalogId,
              categoryId: category.categoryId,
              storeId: category.storeId,
            })),
          },
        ],
      }),
    );
    setRemoved(null);
    setChecked(emptyByPane<string[]>([]));
  };

  // Unlink rewrites the target's set without the rows picked, or without the
  // whole set when nothing is picked. A category that belongs to no set has
  // nothing stored to remove.
  const handleUnlink = () => {
    const linkedCategorySetId = targetSetId;
    if (!linkedCategorySetId) return;
    const picked = checked.target.length
      ? targetSet.filter((category) =>
          checked.target.includes(categoryKey(category)),
        )
      : targetSet;
    const remaining = targetSet.filter(
      (category) =>
        !picked.some((member) => categoryKey(member) === categoryKey(category)),
    );
    clearSourceOnLink.current = false;
    setRemoved(picked.length);
    setEditedSetId(linkedCategorySetId);
    dispatch(
      addLinkedCategorySetsRequest({
        isUnlinkRequest: true,
        removedCount: picked.length,
        linkedCategorySets: [
          {
            linkedCategorySetId,
            categories: remaining.map((category) => ({
              catalogId: category.catalogId,
              categoryId: category.categoryId,
              storeId: category.storeId,
            })),
          },
        ],
      }),
    );
    setChecked(emptyByPane<string[]>([]));
  };

  return (
    <Container>
      {linking && (
        <LinkingOverlay>
          <CircularProgress size={40} />
        </LinkingOverlay>
      )}
      <SearchPane
        pane="target"
        selected={selected.target}
        onSelect={(category) => handleSelect("target", category)}
        categorySet={targetSet}
        checked={checked.target}
        onRowClick={(key, event) => handleRowClick("target", key, event)}
        onUnlink={handleUnlink}
        unlinkDisabled={targetSet.length === 0 || linking}
        statusOverride={
          linked
            ? removed === null
              ? intl.formatMessage({
                  id: "linkedCategories.linkComplete",
                  defaultMessage: "Linked categories updated.",
                })
              : intl.formatMessage(
                  {
                    id: "linkedCategoriesSearch.categoriesRemoved",
                    defaultMessage:
                      "{count, plural, one {# category} other {# categories}} removed.",
                  },
                  { count: removed },
                )
            : undefined
        }
      />
      <ActionColumn>
        <ActionButtonBox>
          <ButtonComponent
            color="primary"
            variant="contained"
            onClick={handleLink}
            disabled={linkDisabled || linking}
            justifyContent="center"
            startIcon={<ArrowBackIcon fontSize="small" />}
          >
            {intl.formatMessage({
              id: "linkedCategoriesSearch.link",
              defaultMessage: "Link",
            })}
          </ButtonComponent>
        </ActionButtonBox>
      </ActionColumn>
      <SearchPane
        pane="source"
        selected={selected.source}
        onSelect={(category) => handleSelect("source", category)}
        categorySet={sourceSet}
        checked={checked.source}
        onRowClick={(key, event) => handleRowClick("source", key, event)}
      />
    </Container>
  );
};

export default LinkedCategoriesSearch;
